import type { PricingInput, PricingResult } from '@deizi/pricing';
import { z } from 'zod';

/* Units: money integer centavos · rates basis points · time integer seconds · grams one decimal ·
   energy decimal R$/kWh. */

const decimals = (places: number) => (v: number) => {
  const f = 10 ** places;
  return Math.abs(v * f - Math.round(v * f)) < 1e-6;
};
const Centavos = z.number().int().min(0).max(100_000_000);
const Bps = z.number().int().min(0).max(10_000);
const Seconds = z
  .number()
  .int()
  .min(0)
  .max(30 * 24 * 3600);
const Grams = z
  .number()
  .min(0)
  .max(100_000)
  .refine(decimals(1), 'Use at most one decimal for grams');
const ReaisPerKwh = z.number().min(0).max(100).refine(decimals(5), 'Use at most 5 decimals');

const FixedFeeRange = z.object({
  fromCentavos: Centavos.describe('Range start, inclusive.'),
  toCentavos: Centavos.nullable().describe(
    'Range end, inclusive ("até R$ 78,99" = 7899); null = no limit.',
  ),
  feeCentavos: Centavos,
});

const ShippingRule = z.object({
  fromCentavos: Centavos.nullable().describe(
    'Seller pays from this price up (free shipping); null = always.',
  ),
  averageCentavos: Centavos.describe('Average shipping the seller pays per unit.'),
});

/** The golden example from CLAUDE.md (Vaso Ondas M on ML Clássico; fixture fees, not real ones). */
export const pricingCalculateExample = {
  printer: {
    powerWatts: 250,
    purchasePriceCentavos: 1_249_900,
    lifetimeHours: 5000,
    maintenancePerHourCentavos: 10,
  },
  energy: { tariffPerKwh: 0.95, bandeiraPerKwh: 0 },
  plates: [
    {
      printSeconds: 11_400,
      unitsPerPlate: 1,
      slots: [{ label: 'PLA', grams: 86, pricePerKgCentavos: 11_990 }],
    },
  ],
  quantity: 1,
  labor: { finishingSecondsPerUnit: 300, packingSecondsPerUnit: 180, ratePerHourCentavos: 3000 },
  design: null,
  supplies: [],
  packagingCentavos: 240,
  rates: {
    wasteBps: 500,
    failureBps: 500,
    targetMarginBps: 2500,
    minimumMarginBps: 1500,
    taxBps: 0,
  },
  adjustments: { rushBps: 0, discountBps: 0 },
  rounding: 'end_90' as const,
  minimumOrderCentavos: 0,
  channels: [
    {
      id: 'ml-classico',
      name: 'ML Clássico',
      commissionBps: 1400,
      paymentFeeBps: 0,
      fixedFees: [{ fromCentavos: 0, toCentavos: 7899, feeCentavos: 625 }],
      shipping: { fromCentavos: 7900, averageCentavos: 2200 },
    },
  ],
} satisfies PricingInput;

const Uuid = z.uuid();

const InlineChannel = z.object({
  id: z.string().min(1).max(100),
  name: z.string().min(1).max(100),
  commissionBps: Bps,
  paymentFeeBps: Bps.default(0),
  fixedFees: z.array(FixedFeeRange).max(50).default([]),
  shipping: ShippingRule.nullable().default(null).describe('null = buyer pays shipping.'),
  targetMarginBps: Bps.nullable()
    .optional()
    .describe('Override the target margin for this channel.'),
  overridePriceCentavos: Centavos.nullable()
    .optional()
    .describe('"Seu preço": evaluate this price instead.'),
});

const StoredChannel = z.object({
  channelId: Uuid.describe('A stored channel; its fee version in effect on `asOf` is used.'),
  categoryId: Uuid.optional().describe('Use this category commission when it has one.'),
  paymentMethod: z.string().max(60).optional().describe('Name of a channel payment method.'),
  targetMarginBps: Bps.nullable().optional(),
  overridePriceCentavos: Centavos.nullable().optional(),
});

const Slot = z
  .object({
    label: z.string().max(100).optional(),
    grams: Grams,
    pricePerKgCentavos: Centavos.optional().describe('Inline price per kg…'),
    filamentId: Uuid.optional().describe('…or a catalog filament (its replacement price is used).'),
  })
  .refine(
    (s) => s.pricePerKgCentavos !== undefined || s.filamentId !== undefined,
    'Give pricePerKgCentavos or filamentId',
  );

export const PricingCalculateRequest = z
  .object({
    printer: z
      .object({
        powerWatts: z
          .number()
          .int()
          .min(0)
          .max(5000)
          .describe('Average draw for this material (PLA ≈ 250 W on the X2D).'),
        purchasePriceCentavos: Centavos,
        lifetimeHours: z.number().int().min(1).max(200_000),
        maintenancePerHourCentavos: Centavos,
      })
      .optional()
      .describe('Inline printer… (or `printerId`).'),
    printerId: Uuid.optional().describe('…or a stored printer.'),
    powerGroup: z
      .enum(['low', 'high'])
      .optional()
      .describe(
        'Stored printer power to use; defaults to the material group of the first catalog filament (else low).',
      ),
    energy: z
      .object({
        tariffPerKwh: ReaisPerKwh.describe('Tariff with taxes, R$/kWh (decimal), e.g. 0.95.'),
        bandeiraPerKwh: ReaisPerKwh.default(0).describe(
          'Bandeira surcharge, R$/kWh (Verde = 0, Amarela ≈ 0.01885).',
        ),
      })
      .optional()
      .describe('Defaults to the shop tariff + bandeira.'),
    plates: z
      .array(
        z.object({
          printSeconds: Seconds,
          unitsPerPlate: z.number().int().min(1).max(1000),
          slots: z.array(Slot).min(1).max(32),
        }),
      )
      .min(1)
      .max(50),
    quantity: z.number().int().min(1).max(100_000),
    labor: z.object({
      finishingSecondsPerUnit: Seconds.describe('Acabamento per unit.'),
      packingSecondsPerUnit: Seconds.describe('Embalagem per unit.'),
      ratePerHourCentavos: Centavos.optional().describe('Defaults to the shop labor rate.'),
    }),
    design: z
      .object({
        seconds: Seconds,
        ratePerHourCentavos: Centavos.optional().describe('Defaults to the shop design rate.'),
      })
      .nullable()
      .default(null)
      .describe('Design fee (modelagem), split across the quantity.'),
    supplies: z
      .array(
        z.union([
          z.object({
            name: z.string().min(1).max(100),
            unitCostCentavos: Centavos,
            quantityPerUnit: z
              .number()
              .min(0)
              .max(10_000)
              .refine(decimals(3), 'Use at most 3 decimals'),
          }),
          z.object({
            supplyId: Uuid,
            quantityPerUnit: z
              .number()
              .min(0)
              .max(10_000)
              .refine(decimals(3), 'Use at most 3 decimals'),
          }),
        ]),
      )
      .max(100)
      .default([]),
    packagingCentavos: Centavos.optional().describe(
      'Packaging cost per unit… (or `packagingKitId`; default 0).',
    ),
    packagingKitId: Uuid.optional().describe('…or a stored packaging kit (its current total).'),
    rates: z
      .object({
        wasteBps: Bps,
        failureBps: Bps,
        targetMarginBps: Bps,
        minimumMarginBps: Bps,
        taxBps: Bps.describe('Per-sale tax (Simples). 0 for MEI: the DAS is a monthly fixed cost.'),
      })
      .partial()
      .optional()
      .describe('Missing rates come from the shop settings (tax from the tax mode).'),
    adjustments: z
      .object({
        rushBps: Bps.describe('Urgência'),
        discountBps: Bps.max(9900).describe('Desconto'),
      })
      .default({ rushBps: 0, discountBps: 0 }),
    rounding: z
      .enum(['end_90', 'end_99', 'whole', 'none'])
      .optional()
      .describe('Friendly price ending (always rounds up); defaults to the shop rule.'),
    minimumOrderCentavos: Centavos.optional().describe(
      '"Preço mínimo por pedido"; defaults to the shop rule.',
    ),
    channels: z
      .array(z.union([StoredChannel, InlineChannel]))
      .min(1)
      .max(30)
      .optional()
      .describe(
        'Inline channels or stored ones; defaults to every active channel shown in Quick Quote.',
      ),
    asOf: z.iso
      .date()
      .optional()
      .describe('Date whose fee versions apply (`YYYY-MM-DD`, São Paulo); defaults to today.'),
  })
  .meta({ id: 'PricingCalculateRequest', example: pricingCalculateExample });
export type PricingCalculateRequest = z.input<typeof PricingCalculateRequest>;

const FeeBreakdown = z.object({
  commission: z.number().int(),
  fixedFee: z.number().int(),
  fixedFeeRange: FixedFeeRange.nullable(),
  shipping: z.number().int(),
  sellerPaysShipping: z.boolean(),
  paymentFee: z.number().int(),
  tax: z.number().int(),
  total: z.number().int(),
});

const PriceEvaluation = z.object({
  priceCentavos: z.number().int(),
  source: z.enum(['suggested', 'override']),
  fees: FeeBreakdown,
  netProfitCentavos: z.number().int(),
  marginBps: z.number().int().describe('Real margin at this price (may be negative).'),
  state: z.enum(['ok', 'below_minimum_margin', 'below_cost']),
});

const ChannelWarning = z.discriminatedUnion('code', [
  z.object({
    code: z.literal('fee_cliff'),
    thresholdCentavos: z.number().int(),
    freeShipping: z.boolean(),
    suggestedPriceCentavos: z.number().int(),
    netProfitCentavos: z.number().int(),
    marginBps: z.number().int(),
  }),
  z.object({
    code: z.literal('below_minimum_order'),
    minimumOrderCentavos: z.number().int(),
    orderTotalCentavos: z.number().int(),
  }),
]);

const ChannelResult = z.object({
  channelId: z.string(),
  name: z.string(),
  targetMarginBps: z.number().int(),
  percentOfPriceBps: z.number().int().describe('commission + payment fee + tax + target margin.'),
  status: z
    .enum(['ok', 'impossible'])
    .describe('`impossible`: no price reaches the target margin on this channel.'),
  suggested: z
    .object({
      rawCentavos: z.number().int().describe('Exact price for the target margin.'),
      adjustedCentavos: z.number().int().describe('After urgência/desconto.'),
      priceCentavos: z.number().int().describe('After friendly rounding (always up).'),
    })
    .nullable(),
  minimumPriceCentavos: z.number().int().nullable(),
  breakEvenPriceCentavos: z.number().int().nullable(),
  evaluation: PriceEvaluation.nullable(),
  warnings: z.array(ChannelWarning),
});

export const PricingCalculateResponse = z
  .object({
    engineVersion: z
      .string()
      .describe('Pricing engine version, stored in quote and order snapshots.'),
    perUnit: z.object({ grams: z.number(), printSeconds: z.number().int() }),
    totals: z.object({
      quantity: z.number().int(),
      plates: z.number().int(),
      grams: z.number(),
      printSeconds: z.number().int(),
    }),
    machinePerHourCentavos: z.number().int(),
    cost: z.object({
      material: z.number().int(),
      machine: z.number().int(),
      machineParts: z.object({
        depreciation: z.number().int(),
        energy: z.number().int(),
        maintenance: z.number().int(),
      }),
      failures: z.number().int(),
      labor: z.number().int(),
      packaging: z.number().int(),
      supplies: z.number().int(),
      design: z.number().int(),
      unitCost: z
        .number()
        .int()
        .describe('Exactly the sum of the rows above (machineParts excluded).'),
    }),
    channels: z.array(ChannelResult),
    feeVersions: z
      .array(
        z.object({ channelId: z.string(), feeVersionId: Uuid, versionNumber: z.number().int() }),
      )
      .describe('Fee version used for each stored channel (for quote and order snapshots).'),
    input: z
      .unknown()
      .describe(
        'The fully resolved engine input (prices, rates, fees), stored in snapshots so the numbers can be reproduced.',
      ),
  })
  .meta({ id: 'PricingCalculateResponse' });
export type PricingCalculateResponse = z.infer<typeof PricingCalculateResponse>;

// Compile-time guards: the API contract and the engine types must not drift apart.
// Any full engine input is a valid request, and every engine result fits the response.
type Assert<T extends true> = T;
export type _EngineInputIsValidRequest = Assert<
  PricingInput extends z.input<typeof PricingCalculateRequest> ? true : false
>;
export type _EngineMatchesResponse = Assert<
  PricingResult extends Omit<z.input<typeof PricingCalculateResponse>, 'feeVersions' | 'input'>
    ? true
    : false
>;
